/**
 * @file select.tsx
 * @description Select-примитив на base-ui (phase08.5).
 *
 * Использование:
 *   <Select value={v} onValueChange={setV}>
 *     <SelectTrigger><SelectValue placeholder="..." /></SelectTrigger>
 *     <SelectContent>
 *       <SelectItem value="a">A</SelectItem>
 *     </SelectContent>
 *   </Select>
 */

import { Select as SelectPrimitive } from "@base-ui/react/select";
import { Check, ChevronDown } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils";

const Select = SelectPrimitive.Root;

/**
 * Значение в триггере. base-ui рендерит сырое value; чтобы показывать
 * русскую подпись — передайте `labels` (карта value → подпись,
 * phase19.8.5). Без labels поведение прежнее (сырое значение).
 */
function SelectValue({
  labels,
  placeholder,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Value> & {
  labels?: Record<string, string>;
}) {
  if (!labels) return <SelectPrimitive.Value placeholder={placeholder} {...props} />;
  return (
    <SelectPrimitive.Value placeholder={placeholder} {...props}>
      {(value: unknown) =>
        value === null || value === undefined || value === ""
          ? (placeholder ?? "")
          : (labels[String(value)] ?? String(value))
      }
    </SelectPrimitive.Value>
  );
}

const SelectTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    data-slot="select-trigger"
    className={cn(
      "flex h-10 w-full items-center justify-between rounded-md bg-input px-3 py-2 text-sm",
      "outline-none transition-all",
      "focus-visible:bg-surface-lowest focus-visible:[box-shadow:inset_0_-2px_0_var(--primary)]",
      "data-[popup-open]:bg-surface-lowest",
      "disabled:cursor-not-allowed disabled:opacity-50",
      "[&>span]:line-clamp-1",
      className,
    )}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon>
      <ChevronDown className="size-4 opacity-50" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = "SelectTrigger";

const SelectContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof SelectPrimitive.Popup>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Portal>
    {/* z-index — на позиционере: попап `position: static` (см. popover.tsx). */}
    <SelectPrimitive.Positioner
      sideOffset={4}
      alignItemWithTrigger
      className="z-[var(--z-popover)]"
    >
      <SelectPrimitive.Popup
        ref={ref}
        data-slot="select-content"
        className={cn(
          "max-h-[var(--available-height)] min-w-[var(--anchor-width)] overflow-y-auto rounded-lg bg-surface-lowest py-1",
          "[box-shadow:0_8px_24px_var(--shadow-ambient),inset_0_0_0_1px_var(--outline-variant)]",
          "data-[ending-style]:opacity-0 data-[starting-style]:opacity-0",
          "transition-opacity duration-150",
          className,
        )}
        {...props}
      >
        {children}
      </SelectPrimitive.Popup>
    </SelectPrimitive.Positioner>
  </SelectPrimitive.Portal>
));
SelectContent.displayName = "SelectContent";

const SelectItem = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    data-slot="select-item"
    className={cn(
      "relative flex w-full cursor-default select-none items-center gap-2 rounded-md py-1.5 pl-3 pr-8 text-sm outline-none",
      "data-[highlighted]:bg-surface-low data-[highlighted]:text-on-surface",
      "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className,
    )}
    {...props}
  >
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    <SelectPrimitive.ItemIndicator className="absolute right-2 flex size-4 items-center justify-center">
      <Check className="size-4" />
    </SelectPrimitive.ItemIndicator>
  </SelectPrimitive.Item>
));
SelectItem.displayName = "SelectItem";

export { Select, SelectValue, SelectTrigger, SelectContent, SelectItem };
